import { Link } from "wouter";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  MODAL_CAPABILITIES,
  isNewlyVerified,
  type Capability,
} from "@/lib/capabilities";
import { CopyInviteButton, INVITE_SHARING_NOTE } from "@/components/invite/copy-invite-button";

const COPY_VARIANTS = {
  newlyVerified: {
    title: "Welcome to the network",
    sub: "Your verified profile is live.",
    callout:
      "One thing worth knowing up front: this network grows by word of mouth, and you're " +
      "encouraged to spread it. If you know ex-Riot people who aren't here yet, your invite " +
      "link gives them a warm way in.",
  },
  veteran: {
    title: "Something new for you",
    sub: "Members can now invite fellow alumni.",
    callout:
      "This network grows by word of mouth. If you know ex-Riot people who aren't here yet, " +
      "your invite link gives them a warm way in. It lives on your dashboard whenever you want it.",
  },
} as const;

function ModalCapabilityCard({
  c,
  onNavigate,
}: {
  c: Capability;
  onNavigate: () => void;
}) {
  const Icon = c.icon;
  const body = (
    <div className="h-full rounded-lg border border-border bg-card p-3.5 transition-colors hover:border-primary/40">
      <div className="mb-1.5 flex items-center gap-2">
        <span className="shrink-0 text-primary">
          <Icon className="h-4 w-4" />
        </span>
        <span className="font-heading text-xs font-semibold leading-tight text-foreground">
          {c.title}
        </span>
      </div>
      <div className="text-[11px] leading-relaxed text-muted-foreground">{c.desc}</div>
    </div>
  );

  if (c.link) {
    return (
      <Link href={c.link} className="block cursor-pointer" onClick={onNavigate}>
        {body}
      </Link>
    );
  }
  return body;
}

interface OnboardingModalProps {
  open: boolean;
  createdAt: string | null | undefined;
  onDismiss: () => void;
  dismissing?: boolean;
}

// Surface 2: post-verification onboarding modal. Title + sub, full-width crimson
// permission callout carrying the "Copy my invite link" action and the sharing
// note, a six-card capability grid (full set minus Invite Alumni), and a single
// "Got it" dismiss. Copy variant is keyed on whether the member is newly verified.
export function OnboardingModal({
  open,
  createdAt,
  onDismiss,
  dismissing,
}: OnboardingModalProps) {
  const newlyVerified = isNewlyVerified(createdAt);
  const copy = newlyVerified ? COPY_VARIANTS.newlyVerified : COPY_VARIANTS.veteran;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onDismiss(); }}>
      <DialogContent className="flex max-h-[85vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-[520px]">
        <div className="px-6 pt-6">
          <h2 className="font-heading text-lg font-bold text-foreground">{copy.title}</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">{copy.sub}</p>
        </div>

        <div className="px-6 pt-4">
          <div className="rounded-lg border border-primary/35 bg-primary/10 p-4">
            <p className="mb-3 text-[13px] leading-relaxed text-foreground">{copy.callout}</p>
            <CopyInviteButton />
            <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
              {INVITE_SHARING_NOTE}
            </p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 pt-4">
          <div className="mb-2.5 font-heading text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            What's on the network
          </div>
          <div className="grid grid-cols-1 gap-2 pb-2 sm:grid-cols-2">
            {MODAL_CAPABILITIES.map((c) => (
              <ModalCapabilityCard
                key={c.id}
                c={c}
                onNavigate={onDismiss}
              />
            ))}
          </div>
        </div>

        <div className="border-t border-border px-6 py-4">
          <Button variant="outline" className="w-full" onClick={onDismiss} disabled={dismissing}>
            Got it
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
